/*
	Hand-rolled sticky-with-bounds for .sidebar, instead of CSS position:sticky.

	Native position:sticky is broken here because it needs its "nearest
	scrolling ancestor" to resolve to the real viewport, but global.css
	declares overflow on BOTH html and body (`overflow-x-hidden
	overflow-y-auto`) - confirmed by direct testing that sticky only works
	once html's own overflow is fully 'visible' on both axes. Removing it
	from html reopens an unrelated, pre-existing bug elsewhere on the site:
	several blocks use a `margin-right: calc(50% - 50vw)` full-bleed swiper
	technique that's ~8px too wide due to the classic 100vw-includes-
	scrollbar quirk, which html's overflow-x:hidden had been silently
	absorbing as a side effect. Fixing that properly is a separate, unrelated
	task, so this block implements sticky manually instead of touching global
	overflow behaviour.
*/
import { fixedHeaderOffset } from '../../src/js/services/ui.service';

// Matches --breakpoint-lg in tailwind.css - .wrap only switches to a row
// (sidebar beside content) from this width up
const DESKTOP_BREAKPOINT = 1364;

// Minimum gap kept between the pinned sidebar and the top of the viewport -
// below a fixed header it pins to the same line anchor-link targets land on
// instead (fixedHeaderOffset(), see theme.js), so it's never overlapped
const MIN_TOP_GAP = 30;

function reset (sidebar) {
	sidebar.style.position = '';
	sidebar.style.top = '';
	sidebar.style.left = '';
	sidebar.style.width = '';
}

function update () {

	const items = [...document.querySelectorAll('section.content-sidebar .wrap')]
		.map(function (wrap) {
			const sidebarWrap = wrap.querySelector(':scope > .sidebar-wrap');
			const sidebar = sidebarWrap ? sidebarWrap.querySelector(':scope > .sidebar') : null;
			return sidebar ? { wrap, sidebarWrap, sidebar } : null;
		})
		.filter(Boolean)
	;

	const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
	const topGap = Math.max (MIN_TOP_GAP, fixedHeaderOffset ());

	items.forEach(function ({wrap, sidebarWrap, sidebar}) {

		// Reset before measuring so this reads the natural (static) layout,
		// not a stale fixed/absolute state from a previous scroll position
		reset (sidebar);

		if (! isDesktop) return;

		const wrapRect = wrap.getBoundingClientRect();
		const slotRect = sidebarWrap.getBoundingClientRect();
		const sidebarHeight = sidebar.offsetHeight;

		if (wrapRect.top >= topGap) return;

		// How far the sidebar could travel down before its bottom edge
		// would pass .wrap's own bottom edge (i.e. run past the content)
		const maxTop = wrapRect.height - sidebarHeight;
		const scrolledPast = topGap - wrapRect.top;

		if (scrolledPast >= maxTop) {
			// Content has scrolled past - pin to the bottom of .wrap instead
			// of continuing to follow the viewport (needs .wrap: relative).
			// Same offset the fixed state would reach at this exact scroll
			// point, so the switch is seamless (no jump).
			sidebar.style.position = 'absolute';
			sidebar.style.top = Math.max (maxTop, 0) + 'px';
			sidebar.style.left = (slotRect.left - wrapRect.left) + 'px';
			sidebar.style.width = slotRect.width + 'px';
		} else {
			sidebar.style.position = 'fixed';
			sidebar.style.top = topGap + 'px';
			sidebar.style.left = slotRect.left + 'px';
			sidebar.style.width = slotRect.width + 'px';
		}

	});

}

let ticking = false;
function onScrollOrResize () {
	if (ticking) return;
	ticking = true;
	requestAnimationFrame (function () {
		update ();
		ticking = false;
	});
}

// The scroll/resize listeners only ever need to be registered once per
// window - contentSidebar() itself gets called again on every ACF preview
// re-render (see below), and re-adding these same listeners on every one of
// those calls (without ever removing the old pair) leaked a permanent new
// listener pair per edit, each repeating the full measure/write pass on
// every scroll/resize for the rest of the editing session - a big part of
// why this block got progressively slower to edit the longer a session went.
let listenersRegistered = false;

export default function contentSidebar () {

	if (! document.querySelector ('section.content-sidebar .sidebar')) return;

	update ();

	if (! listenersRegistered) {
		window.addEventListener ('scroll', onScrollOrResize, {passive: true});
		window.addEventListener ('resize', onScrollOrResize);
		listenersRegistered = true;
	}

}



/*
	ACF Blocks v3 re-fetches and re-renders this block's preview markup
	inside the editor (eg. on attribute change) without a page reload, so
	'DOMContentLoaded' only covers the first render - re-run init on every
	preview refresh too. ACF's block-preview JS may run in the iframe's own
	window or in the parent admin window depending on context, so check both.
*/
const acfInstance = (typeof window !== 'undefined')
	? (window.acf || (window.parent !== window && window.parent.acf))
	: null
;

if (acfInstance) {
	acfInstance.addAction ('render_block_preview/type=content-sidebar', contentSidebar);
}
